import { AssetIcon } from '@/components/AssetIcon';
import { AssetBonusDisplay } from '@/components/AssetBonusDisplay';
import { GuideMenu } from '@/components/GuideMenu';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  getGuideBonusesData,
  type BonusDefinition,
  type TraitBonus,
} from '@/lib/bonuses-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

// Content sections components for better organization
const IntroSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      An Overview of Races and Advisors
    </h3>
    <div>
      <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
        Choosing Your Race
      </h4>
      <p className="mb-4">
        When selecting your race, consider how it aligns with your intended
        strategy and playstyle. Each race brings unique strengths and
        characteristics that will shape your journey in Mythea.
      </p>
      <p>
        As you grow in experience, you will develop your own unique playstyle
        and gain a deeper understanding of the strengths and nuances of each
        race. Experimentation will be key to finding the perfect match for your
        strategy.
      </p>
    </div>
  </div>
);

const AdvisorsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      The Advisors – Assembling Your Council
    </h3>
    <p>
      Your race not only shapes your province but also determines the advisors
      available to serve on your council. These skilled individuals offer unique
      abilities that can aid you in governance, warfare, economy, and magic.
    </p>
    <ul className="list-disc list-inside space-y-2 pl-4">
      <li>
        While under protection or during the jubilant phase, you may freely add
        and remove advisors as often as you wish.
      </li>
      <li>
        Once those phases end, an 8-hour delay is imposed between each advisor
        change—so choose wisely!
      </li>
    </ul>
    <p className="italic">
      A ruler is only as strong as their council. Select those who will best
      support your ambitions, and do not hesitate to adapt as your province
      evolves.
    </p>
  </div>
);

const RacesSection = ({ raceCount }: { raceCount: number | null }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      The Races of Mythea
    </h3>
    <p>
      The world of Mythea is home to a vast array of creatures—some dwelling in
      the mountains, others ruling the seas, and some even soaring through the
      skies. However, few have built true civilizations, forging societies fit
      to be led by one such as yourself.
    </p>
    <p>
      The current age race list is loaded from the canonical age reference
      below. Upon joining, you must choose one, shaping the strengths and
      culture of your province. Your race will influence everything—from your
      economy and combat prowess to your magical aptitude and resilience.
    </p>
    {raceCount !== null ? (
      <p>
        The live reference currently contains <strong>{raceCount} races</strong>.
      </p>
    ) : null}
    <p className="italic">
      Choose wisely, for your race will define much of your journey in Mythea.
    </p>
  </div>
);

function RaceCard({
  race,
  bonusDefinitionsById,
}: {
  race: TraitBonus;
  bonusDefinitionsById: Map<number, BonusDefinition>;
}) {
  return (
    <Card className="p-4">
      <div className="flex flex-col gap-4 sm:flex-row">
        <AssetIcon
          assetCode={race.name}
          assetType="race"
          size="small"
          onError={() => {
            // AssetIcon falls back to the default image.
          }}
        />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="font-cinzel text-lg text-[#f0f0f0]">{race.name}</h3>
            <p className="mt-1 text-sm text-[#b8b8b8]">{race.description}</p>
          </div>
          <AssetBonusDisplay
            asset={race}
            bonusDefinitionsById={bonusDefinitionsById}
          />
        </div>
      </div>
    </Card>
  );
}

export const Route = createFileRoute('/races/')({
  component: RacesPage,
});

function RacesPage() {
  const bonusesQuery = useQuery({
    queryKey: ['guide-bonuses', 'races'],
    queryFn: getGuideBonusesData,
    staleTime: 1000 * 60 * 10,
  });

  const races = bonusesQuery.data?.races || [];
  const bonusDefinitionsById = new Map(
    (bonusesQuery.data?.bonusDefinitions || []).map(definition => [
      definition.id,
      definition,
    ]),
  );

  return (
    <div className="min-h-screen bg-black text-white p-2 sm:p-4">
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-8">
        <GuideMenu currentPath="/races" />

        {/* Main content section */}
        <section className="bg-white/[0.04] p-4 sm:p-6 rounded-lg border border-[rgba(212,175,55,0.12)]">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 sm:mb-8">
            <AssetIcon
              assetCode="dark_elf"
              assetType="race"
              size="medium"
              onError={() => {
                // Silent error handler
              }}
            />
            <h2 className="text-xl sm:text-2xl font-bold">Races</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-8 sm:space-y-12 text-[#b8b8b8] leading-relaxed text-sm sm:text-base">
            <IntroSection />
            <AdvisorsSection />
            <RacesSection raceCount={bonusesQuery.data ? races.length : null} />
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Current age</Badge>
            {bonusesQuery.data ? <Badge>{bonusesQuery.data.ageName}</Badge> : null}
            {bonusesQuery.data ? <Badge>Source: {bonusesQuery.data.source}</Badge> : null}
          </div>

          {bonusesQuery.isLoading ? (
            <Card className="p-4 text-sm text-[#b8b8b8]">
              Loading current age races...
            </Card>
          ) : null}

          {bonusesQuery.isError ? (
            <Card className="p-4 text-sm text-[#b8b8b8]">
              Current age race data could not be loaded. The guide app needs
              the published bonuses and strings bucket manifests to render this
              reference.
            </Card>
          ) : null}

          {bonusesQuery.data && races.length ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {races.map(race => (
                <RaceCard
                  key={race.id}
                  race={race}
                  bonusDefinitionsById={bonusDefinitionsById}
                />
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
